import { Injectable, computed, signal } from '@angular/core';
import { Tab } from '../models/tab.model';

@Injectable({ providedIn: 'root' })
export class TabService {
  readonly tabs = signal<Tab[]>([]);
  readonly activeTabId = signal<string | null>(null);

  readonly activeTab = computed(
    () => this.tabs().find((t) => t.id === this.activeTabId()) ?? null,
  );

  openTab(params: { id: string; title: string; route: string; closeable?: boolean }): void {
    const existing = this.tabs().find((t) => t.id === params.id);
    if (existing) {
      this.activeTabId.set(existing.id);
      return;
    }
    const newTab: Tab = {
      id: params.id,
      title: params.title,
      route: params.route,
      closeable: params.closeable ?? true,
    };
    this.tabs.update((tabs) => [...tabs, newTab]);
    this.activeTabId.set(newTab.id);
  }

  closeTab(id: string): string | null {
    const tabs = this.tabs();
    const idx = tabs.findIndex((t) => t.id === id);
    if (idx === -1) return this.activeTab()?.route ?? null;

    this.tabs.update((t) => t.filter((tab) => tab.id !== id));

    if (this.activeTabId() === id) {
      const newTabs = this.tabs();
      if (newTabs.length > 0) {
        const newIdx = Math.max(0, Math.min(idx, newTabs.length - 1));
        this.activeTabId.set(newTabs[newIdx].id);
        return newTabs[newIdx].route;
      }
      this.activeTabId.set(null);
      return null;
    }
    return this.activeTab()?.route ?? null;
  }

  activateTab(id: string): void {
    this.activeTabId.set(id);
  }

  closeOtherTabs(keepId: string): string | null {
    this.tabs.update((tabs) => tabs.filter((t) => t.id === keepId));
    this.activeTabId.set(keepId);
    return this.tabs()[0]?.route ?? null;
  }

  closeTabsToRight(id: string): string | null {
    const tabs = this.tabs();
    const idx = tabs.findIndex((t) => t.id === id);
    if (idx === -1) return this.activeTab()?.route ?? null;
    this.tabs.update((t) => t.slice(0, idx + 1));
    const remaining = this.tabs();
    if (!remaining.find((t) => t.id === this.activeTabId())) {
      const last = remaining[remaining.length - 1];
      this.activeTabId.set(last?.id ?? null);
      return last?.route ?? null;
    }
    return this.activeTab()?.route ?? null;
  }

  closeAllTabs(): void {
    this.tabs.set([]);
    this.activeTabId.set(null);
  }
}
